import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useVolver } from '@/shared/hooks/useVolver';
import { Button, Field } from '@/shared/ui';
import { BarcodeIcon, CloseIcon, GalleryIcon } from '@/shared/ui/icons';
import { captureStore } from './captureStore';
import { useCamera } from './useCamera';

type Mode = 'code' | 'dish' | 'label';

const MODES: ReadonlyArray<{ value: Mode; label: string }> = [
  { value: 'code', label: 'Código' },
  { value: 'dish', label: 'Plato' },
  { value: 'label', label: 'Rótulo' },
];

/** RF-11 · las tres simbologías que el requerimiento nombra, y ninguna más. */
const FORMATS = ['ean_8', 'ean_13', 'upc_a'];

/** CU-03 E1 · pasados diez segundos sin decodificar, se ofrece el ingreso manual. */
const GIVE_UP_AFTER_MS = 10_000;
const SCAN_INTERVAL_MS = 250;
const SWIPE_THRESHOLD_PX = 40;

/**
 * S-02 · la captura, a pantalla completa.
 *
 * Tres modos y un solo gesto para cambiarlos. Los rótulos de abajo son confirmación de dónde
 * estás parado, no un menú: abrir un desplegable para elegir entre tres cosas, con el teléfono en
 * una mano y el plato enfriándose, es exactamente la fricción que este producto no se puede
 * permitir.
 */
export function CapturePage() {
  const navigate = useNavigate();
  const volver = useVolver('/');
  const [mode, setMode] = useState<Mode>('dish');
  const [manualEntry, setManualEntry] = useState(false);
  const [manualEan, setManualEan] = useState('');
  const [scanTimedOut, setScanTimedOut] = useState(false);
  const [busy, setBusy] = useState(false);

  const { videoRef, status, capture } = useCamera(true);
  const swipeOrigin = useRef<number | null>(null);
  const galleryInput = useRef<HTMLInputElement>(null);

  const goToDeclaration = useCallback(
    (files: File[], ean: string | null, role: 'Dish' | 'NutritionLabel') => {
      captureStore.set({
        files,
        role,
        ean,
        previewUrls: files.map((file) => URL.createObjectURL(file)),
      });
      /*
       * `replace` y no un empujón más.
       *
       * Registrar es un asistente de tres pasos —cámara, declaración, detalle— y los tres se
       * pisan entre sí. Sin esto, volver desde el detalle llevaba de nuevo a la cámara: sacabas la
       * foto, registrabas, tocabas volver y estabas otra vez sacando una foto. El asistente
       * colapsa a una sola entrada, así que volver sale a donde se entró.
       */
      navigate('/registrar', { replace: true });
    },
    [navigate],
  );

  // Escaneo continuo mientras el modo es «Código». Se apaga solo al cambiar de modo.
  useEffect(() => {
    if (mode !== 'code' || status !== 'live' || window.BarcodeDetector === undefined) {
      return;
    }

    const detector = new window.BarcodeDetector({ formats: FORMATS });
    let cancelled = false;

    const scan = async () => {
      const video = videoRef.current;
      if (cancelled || video === null || video.readyState < video.HAVE_CURRENT_DATA) {
        return;
      }

      try {
        const [found] = await detector.detect(video);
        if (found !== undefined && !cancelled) {
          cancelled = true;
          goToDeclaration([], found.rawValue, 'Dish');
        }
      } catch {
        // Un cuadro que el detector no puede leer es lo normal mientras se enfoca. Sólo el reloj
        // de diez segundos decide que hay que ofrecer el teclado.
      }
    };

    const scanTimer = window.setInterval(() => void scan(), SCAN_INTERVAL_MS);
    const giveUpTimer = window.setTimeout(() => setScanTimedOut(true), GIVE_UP_AFTER_MS);

    return () => {
      cancelled = true;
      window.clearInterval(scanTimer);
      window.clearTimeout(giveUpTimer);
      setScanTimedOut(false);
    };
  }, [mode, status, videoRef, goToDeclaration]);

  const shoot = async () => {
    setBusy(true);
    const file = await capture();
    setBusy(false);

    if (file !== null) {
      goToDeclaration([file], null, mode === 'label' ? 'NutritionLabel' : 'Dish');
    }
  };

  const shiftMode = (delta: number) => {
    const index = MODES.findIndex((candidate) => candidate.value === mode);
    const next = MODES[Math.min(MODES.length - 1, Math.max(0, index + delta))];
    if (next !== undefined) {
      setMode(next.value);
    }
  };

  return (
    <div className="capture">
      <div
        className="capture-viewfinder"
        onPointerDown={(event) => {
          swipeOrigin.current = event.clientX;
        }}
        onPointerUp={(event) => {
          const origin = swipeOrigin.current;
          swipeOrigin.current = null;
          if (origin === null) {
            return;
          }

          const travelled = event.clientX - origin;
          if (Math.abs(travelled) >= SWIPE_THRESHOLD_PX) {
            shiftMode(travelled < 0 ? 1 : -1);
          }
        }}
      >
        <video ref={videoRef} autoPlay playsInline muted aria-label="Vista de la cámara" />
        <span className="capture-scrim" />
        <span className="capture-guide" />

        <div className="capture-top">
          <button
            type="button"
            className="glass glass-square"
            aria-label="Cerrar"
            onClick={volver}
          >
            <CloseIcon size={18} />
          </button>

          {status === 'denied' && <span className="glass">Sin acceso a la cámara</span>}
        </div>

        <div className="capture-bottom">
          {/*
            Sin permiso de cámara la pantalla era un acertijo: un chip que decía «Sin acceso»,
            el obturador muerto y ninguna palabra sobre cómo seguir. Las salidas existían —la
            galería y anotar sin foto— pero una era un ícono sin etiqueta y la otra ni aparecía.
            Registrar la comida no depende de la cámara (RF-09) y la pantalla lo tiene que decir.
          */}
          {status === 'denied' && !manualEntry && (
            <div className="capture-limit">
              <strong className="small">La cámara está bloqueada</strong>
              <span className="micro" style={{ opacity: 0.8 }}>
                Podés habilitarla desde los permisos del navegador, o seguir sin ella.
              </span>
              <Button variant="primary" onClick={() => galleryInput.current?.click()}>
                Elegir una foto de la galería
              </Button>
              <Button variant="quiet" onClick={() => goToDeclaration([], null, 'Dish')}>
                Anotar sin foto
              </Button>
            </div>
          )}

          {mode === 'code' && scanTimedOut && !manualEntry && (
            <div className="capture-limit">
              <strong className="small">No se pudo leer el código</strong>
              <span className="micro" style={{ opacity: 0.8 }}>
                Seguí intentando, o escribí los dígitos a mano.
              </span>
              <Button variant="primary" onClick={() => setManualEntry(true)}>
                Escribir el código
              </Button>
            </div>
          )}

          {manualEntry ? (
            <div className="capture-limit">
              <Field
                label="Código de barras"
                inputMode="numeric"
                autoFocus
                value={manualEan}
                onChange={(event) => setManualEan(event.target.value)}
              />
              <div className="row">
                <Button
                  variant="primary"
                  disabled={manualEan.trim() === ''}
                  onClick={() => goToDeclaration([], manualEan.trim(), 'Dish')}
                >
                  Continuar
                </Button>
                <Button variant="quiet" onClick={() => setManualEntry(false)}>
                  Volver a la cámara
                </Button>
              </div>
            </div>
          ) : (
            <>
              <div className="capture-modes" role="group" aria-label="Modo de captura">
                {MODES.map((candidate) => (
                  <button
                    key={candidate.value}
                    type="button"
                    className="capture-mode"
                    aria-pressed={candidate.value === mode}
                    onClick={() => setMode(candidate.value)}
                  >
                    {candidate.label}
                    <span className="capture-mode-dot" />
                  </button>
                ))}
              </div>

              <div className="capture-controls">
                {/*
                  La galería, en el hueco que hasta ahora era un espaciador.
                  Comer y registrar no siempre pasan al mismo tiempo: la foto de la cena de anoche
                  ya está sacada, y obligar a fotografiar el plato en vivo dejaba fuera todo lo que
                  se registra más tarde. En modo rótulo sigue subiendo como rótulo.
                */}
                <button
                  type="button"
                  className="glass glass-square"
                  aria-label="Elegir una foto de la galería"
                  disabled={mode === 'code'}
                  onClick={() => galleryInput.current?.click()}
                >
                  <GalleryIcon size={18} />
                </button>

                <button
                  type="button"
                  className="capture-shutter"
                  aria-label={mode === 'label' ? 'Fotografiar el rótulo' : 'Fotografiar el plato'}
                  disabled={status !== 'live' || busy || mode === 'code'}
                  onClick={() => void shoot()}
                />

                <button
                  type="button"
                  className="glass glass-square"
                  aria-label="Escribir el código a mano"
                  onClick={() => setManualEntry(true)}
                >
                  <BarcodeIcon size={18} />
                </button>
              </div>

              <span className="micro" style={{ opacity: 0.7 }}>
                {mode === 'code'
                  ? 'Apuntá al código de barras del envase'
                  : 'Desplazá el visor para cambiar de modo'}
              </span>
            </>
          )}
        </div>
      </div>

      {/*
        Sin el atributo `capture`. Ponerlo obliga al sistema operativo a abrir la cámara y le
        esconde la galería a la persona, que es justo lo contrario de lo que este control existe
        para hacer.
      */}
      {/*
        `multiple` por RF-10. Un plato entra mal en una sola foto y el modelo estima mejor con dos
        vistas; elegirlas de a una, volviendo a la cámara cada vez, era la forma cara de hacer lo
        mismo.
      */}
      <input
        ref={galleryInput}
        type="file"
        accept="image/*"
        multiple
        hidden
        onChange={(event) => {
          const files = [...(event.target.files ?? [])];
          event.target.value = '';

          if (files.length > 0) {
            goToDeclaration(files, null, mode === 'label' ? 'NutritionLabel' : 'Dish');
          }
        }}
      />
    </div>
  );
}
